import { useTranslation } from "react-i18next";

import { formatDate, formatDateAt } from "simplydo/core/dates";
import { cx, styled } from "simplydo/react/styled";
import { Icon } from "simplydo/react/ui/icon";

import { UserChip } from "components/lib/Chips";
import FormattedComment from "components/lib/FormattedComment";

import {
  IProjectIdeaEvent,
  eventTypes,
  getLaneNameFromEvent,
  hasEventType,
} from "./ideaEventUtils";

const impactStageLabels: Record<string, string> = {
  userEstimate: "user estimate",
  projectEstimate: "project estimate",
  finalOutcome: "final outcome",
};

const IdeaName = ({
  event,
  show,
}: {
  event: IProjectIdeaEvent;
  show: boolean;
}) => {
  const { t } = useTranslation();
  return show ? (
    <b>{event.targetIdea?.name ?? t("generic.ideaWithArticle")}</b>
  ) : null;
};

const EventItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 5px;
  padding: 6px 12px;

  &.is-interacted {
    background-color: #fff;
  }

  &:not(.is-interacted) {
    background-color: #f1f1f1;
  }

  &.is-clickable {
    cursor: pointer;

    &:hover {
      background-color: #f8f8f8;
    }
  }

  &:not(.is-clickable) {
    cursor: default;
  }

  .icon-container {
    .i {
      font-size: 0.9em;
      line-height: 1.1em;
    }
    margin-right: 10px;
  }

  .info-container {
    > span {
      display: block;
      line-height: 1.3em;
    }
    .meta {
      color: gray;
      font-size: 0.85em;
      margin-bottom: 2px;
    }
  }
`;

type IdeaProjectEventMessageProps = {
  event: IProjectIdeaEvent;
  showIdeaName?: boolean;
  defaultLaneName: string;
  commentDisplay?: "truncated" | "formatted";
};

const IdeaProjectEventMessage = ({
  event,
  showIdeaName = true,
  defaultLaneName,
  commentDisplay = "truncated",
}: IdeaProjectEventMessageProps) => {
  const { t } = useTranslation();

  const getLaneName = (
    laneKey: "previousLane" | "targetLane" = "previousLane",
  ) => getLaneNameFromEvent(event, defaultLaneName, laneKey);

  const formatDeadline = (value?: string) =>
    value ? formatDate(value, "dd/MM/yyyy") : "No deadline";

  const formatReviewDate = (value?: string) =>
    value ? formatDate(value, "dd/MM/yyyy") : "No review date";

  const impactStage = event.stage ? (
    <b>{impactStageLabels[event.stage] ?? event.stage}</b>
  ) : null;

  return (
    <>
      {event.action === "finalOutcomeSubmitted" ? (
        // Submitted through an email link, so the user is the manager who sent it
        <>
          <b>{event.email}</b>
          {" submitted the final outcome"}
          {showIdeaName ? (
            <>
              {" for "}
              <IdeaName event={event} show />
            </>
          ) : null}
          {" via a link from "}
          <UserChip compact="very" user={event.userObject} />
          {"."}
        </>
      ) : event.automationObject ? (
        <b>
          {t("challenge.settings.automations.eventActor", {
            name: event.automationObject.name,
          })}
        </b>
      ) : (
        <UserChip compact="very" user={event.userObject} />
      )}
      {event.action === "addedToBoard" &&
        ` added the ${t("generic.idea")} to the board`}
      {event.action === "removedFromBoard" &&
        ` removed the ${t("generic.idea")} from the board`}
      {event.action === "assignedExternalUser" && (
        <>
          {" invited "}
          <b>{event.email ?? "unknown user email"}</b>
          {showIdeaName && (
            <>
              {" to "}
              <IdeaName event={event} show />
            </>
          )}
          {"."}
        </>
      )}
      {event.action === "assignedUser" && (
        <>
          {event.userObject ? " assigned " : " auto-assigned "}
          <UserChip compact="very" user={event.targetUserObject} />
          {showIdeaName && (
            <>
              {" to "}
              <IdeaName event={event} show />
            </>
          )}
          {"."}
        </>
      )}
      {event.action === "unassignedUser" && (
        <>
          {" un-assigned "}
          <UserChip compact="very" user={event.targetUserObject} />
          {showIdeaName && (
            <>
              {" from "}
              <IdeaName event={event} show />
            </>
          )}
          {"."}
        </>
      )}
      {event.action === "changedLane" && (
        <>
          {showIdeaName ? (
            <>
              {" moved "}
              <IdeaName event={event} show />
            </>
          ) : (
            " moved"
          )}
          {" from "}
          <b>{getLaneName()}</b>
          {" to "}
          <b>{getLaneName("targetLane")}</b>
          {"."}
        </>
      )}
      {event.action === "changedDeadline" && (
        <>
          {showIdeaName ? (
            <>
              {" changed the deadline of "}
              <IdeaName event={event} show />
            </>
          ) : (
            " changed the deadline"
          )}
          {" to "}
          <b>{formatDeadline(event.deadline)}</b>
          {"."}
        </>
      )}
      {event.action === "changedReviewDate" && (
        <>
          {showIdeaName ? (
            <>
              {" changed the review date of "}
              <IdeaName event={event} show />
            </>
          ) : (
            " changed the review date"
          )}
          {" to "}
          <b>{formatReviewDate(event.reviewDate)}</b>
          {"."}
        </>
      )}
      {event.action === "createComment" &&
        (commentDisplay === "formatted" ? (
          <>
            {" added a project note. "}
            <span
              style={{
                maxWidth: "520px",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Note: <FormattedComment>{event.comment}</FormattedComment>
            </span>
          </>
        ) : (
          <>
            {showIdeaName ? (
              <>
                {" added a project note to "}
                <IdeaName event={event} show />
              </>
            ) : (
              " added a project note"
            )}
            {". Note: "}
            <b>
              &quot;
              {event.comment && event.comment.length > 50
                ? `${event.comment.substring(0, 50)}...`
                : event.comment}
              &quot;
            </b>
          </>
        ))}
      {event.action === "assessorAssigned" && (
        <>
          {" assigned "}
          <UserChip compact="very" user={event.targetUserObject} />
          {" as assessor"}
          {showIdeaName && (
            <>
              {" to "}
              <IdeaName event={event} show />
            </>
          )}
          {"."}
        </>
      )}
      {event.action === "assessorUnassigned" && (
        <>
          {" unassigned "}
          <UserChip compact="very" user={event.targetUserObject} />
          {" as assessor"}
          {showIdeaName && (
            <>
              {" from "}
              <IdeaName event={event} show />
            </>
          )}
          {"."}
        </>
      )}
      {event.action === "assessorInvited" && (
        <>
          {" invited "}
          <b>{event.invitee ?? "an assessor"}</b>
          {" to assess"}
          {showIdeaName && (
            <>
              {" "}
              <IdeaName event={event} show />
            </>
          )}
          {"."}
        </>
      )}
      {event.action === "ideaAssessorAssigned" && (
        <>
          {" was assigned as assessor"}
          {showIdeaName && (
            <>
              {" to "}
              <IdeaName event={event} show />
            </>
          )}
          {"."}
        </>
      )}
      {event.action === "assessmentSubmitted" && (
        <>
          {" submitted an assessment"}
          {showIdeaName && (
            <>
              {" for "}
              <IdeaName event={event} show />
            </>
          )}
          {"."}
        </>
      )}
      {event.action === "assessmentHasConflict" && (
        <>
          {" declared a conflict of interest"}
          {showIdeaName && (
            <>
              {" for "}
              <IdeaName event={event} show />
            </>
          )}
          {"."}
        </>
      )}
      {event.action === "sentAssessmentFeedback" && (
        <>
          {" sent assessment feedback"}
          {showIdeaName && (
            <>
              {" for "}
              <IdeaName event={event} show />
            </>
          )}
          {" to the authors"}
          {"."}
        </>
      )}
      {event.action === "ideaSubmitted" && (
        <>
          {showIdeaName ? (
            <>
              {" submitted "}
              <IdeaName event={event} show />
            </>
          ) : (
            ` submitted the ${t("generic.idea")}`
          )}
          {"."}
        </>
      )}
      {event.action === "ideaUnsubmitted" && (
        <>
          {showIdeaName ? (
            <>
              {" withdrew "}
              <IdeaName event={event} show />
            </>
          ) : (
            ` unsubmitted the ${t("generic.idea")}`
          )}
          {"."}
        </>
      )}
      {event.action === "labelsAdded" && (
        <>
          {showIdeaName ? (
            <>
              {" added labels to "}
              <IdeaName event={event} show />
            </>
          ) : (
            " added labels"
          )}
          {event.labels?.length ? (
            <>
              {": "}
              <b>{event.labels.join(", ")}</b>
            </>
          ) : null}
          {"."}
        </>
      )}
      {event.action === "labelsRemoved" && (
        <>
          {showIdeaName ? (
            <>
              {" removed labels from "}
              <IdeaName event={event} show />
            </>
          ) : (
            " removed labels"
          )}
          {event.labels?.length ? (
            <>
              {": "}
              <b>{event.labels.join(", ")}</b>
            </>
          ) : null}
          {"."}
        </>
      )}
      {event.action === "isPinnedUpdated" && (
        <>
          {event.isPinned ? " pinned " : " unpinned "}
          {showIdeaName ? (
            <IdeaName event={event} show />
          ) : (
            `the ${t("generic.idea")}`
          )}
          {"."}
        </>
      )}
      {event.action === "changedSubmissionDeadline" && (
        <>
          {showIdeaName ? (
            <>
              {" changed the submission deadline of "}
              <IdeaName event={event} show />
            </>
          ) : (
            " changed the submission deadline"
          )}
          {" to "}
          <b>{formatDeadline(event.deadline)}</b>
          {"."}
        </>
      )}
      {event.action === "sentAuthorEmail" && (
        <>
          {" sent an email to the authors"}
          {showIdeaName && (
            <>
              {" of "}
              <IdeaName event={event} show />
            </>
          )}
          {!showIdeaName && ` of the ${t("generic.idea")}`}
          {"."}
        </>
      )}
      {event.action === "impactStatementCreated" && (
        <>
          {" created an impact statement"}
          {event.stage ? (
            <>
              {" ("}
              {impactStage}
              {")"}
            </>
          ) : null}
          {showIdeaName ? (
            <>
              {" for "}
              <IdeaName event={event} show />
            </>
          ) : null}
          {"."}
        </>
      )}
      {event.action === "impactStatementDeleted" && (
        <>
          {" deleted an impact statement"}
          {event.stage ? (
            <>
              {" ("}
              {impactStage}
              {")"}
            </>
          ) : null}
          {showIdeaName ? (
            <>
              {" for "}
              <IdeaName event={event} show />
            </>
          ) : null}
          {"."}
        </>
      )}
    </>
  );
};

export type IdeaProjectEventItemProps = {
  event: IProjectIdeaEvent;
  showIdeaName?: boolean;
  defaultLaneName?: string;
  onClick?: () => void;
  interacted?: boolean;
  commentDisplay?: "truncated" | "formatted";
};

const IdeaProjectEventItem = ({
  event,
  showIdeaName = true,
  defaultLaneName = "Default",
  onClick,
  interacted,
  commentDisplay = "truncated",
}: IdeaProjectEventItemProps) => {
  if (!hasEventType(event.action)) return null;

  const eventType = eventTypes[event.action];

  return (
    <div
      style={{
        marginBottom: 4,
        paddingBottom: 4,
        borderBottom: "1px solid #f1f1f1",
      }}
    >
      <EventItem
        className={cx(onClick && "is-clickable", interacted && "is-interacted")}
        onClick={onClick}
      >
        <div className="icon-container">
          <Icon icon={eventType.icon} />
        </div>
        <div className="info-container">
          <span className="meta">{formatDateAt(event.createdAt)}</span>
          <span>
            <IdeaProjectEventMessage
              event={event}
              showIdeaName={showIdeaName}
              defaultLaneName={defaultLaneName}
              commentDisplay={commentDisplay}
            />
          </span>
        </div>
      </EventItem>
    </div>
  );
};

export default IdeaProjectEventItem;
